import React, { useEffect, useState, useMemo } from "react";
import { Box, CircularProgress, Grid, TextField, Autocomplete, Button } from "@mui/material";
import {  getCaptainOrderUsers, getDeliveryUsers } from "../../../../../../services/Admin/SalesScreenApi";
import { getUsersAccountsList } from "../../../../../../services/Admin/UsersAccountsApi";
const OrderCustomerInfo = ({ formData, handleFieldChange, loading }) => {
	const [customers, setCustomers] = useState([]);
	const [loadingCustomers, setLoadingCustomers] = useState(false);
	const [searchInput, setSearchInput] = useState("");
	const [captains, setCaptains] = useState([]);
	const [loadingCaptains, setLoadingCaptains] = useState(false);
	const [deliveryUsers, setDeliveryUsers] = useState([]);
	const [loadingDeliveryUsers, setLoadingDeliveryUsers] = useState(false);
	const [dateInitialized, setDateInitialized] = useState(false);
	const [showAdditionalFields, setShowAdditionalFields] = useState(false);
	const [showAdditionalAddresses, setShowAdditionalAddresses] = useState(false);

	useEffect(() => {
		const fetchCustomers = async () => {
			setLoadingCustomers(true);
			try {
				const response = await getUsersAccountsList();
				const customersList = Array.isArray(response?.data) ? response.data : [];
				setCustomers(customersList.filter((customer) => customer?.type === "user"));
			} catch (error) {
				console.error("فشل تحميل العملاء:", error);
				setCustomers([]);
			} finally {
				setLoadingCustomers(false);
			}
		};

		fetchCustomers();
	}, []);

	// تحميل قائمة captain_order
	useEffect(() => {
		const fetchCaptains = async () => {
			setLoadingCaptains(true);
			try {
				const response = await getCaptainOrderUsers();
				const captainsList = Array.isArray(response?.data) ? response.data : [];
				setCaptains(captainsList);
			} catch (error) {
				console.error("فشل تحميل قادة الطلب:", error);
				setCaptains([]);
			} finally {
				setLoadingCaptains(false);
			}
		};

		fetchCaptains();
	}, []);

	// تحميل قائمة delivery_users
	useEffect(() => {
		const fetchDeliveryUsers = async () => {
			setLoadingDeliveryUsers(true);
			try {
				const response = await getDeliveryUsers();
				const deliveryList = Array.isArray(response?.data) ? response.data : [];
				setDeliveryUsers(deliveryList);
			} catch (error) {
				console.error("فشل تحميل موظفي التوصيل:", error);
				setDeliveryUsers([]);
			} finally {
				setLoadingDeliveryUsers(false);
			}
		};

		fetchDeliveryUsers();
	}, []);

	// عند اختيار عميل، ملء بيانات العميل في النموذج
	useEffect(() => {
		if (formData.customer_id && customers.length > 0) {
			const selectedCustomer = customers.find(
				(customer) => customer.id === formData.customer_id
			);

			if (selectedCustomer) {
				// ملء جميع حقول بيانات العميل
				handleFieldChange({
					target: { name: "name", value: selectedCustomer.name || "" },
				});
				handleFieldChange({
					target: { name: "phone", value: selectedCustomer.phone || "" },
				});
				handleFieldChange({
					target: { name: "phone2", value: selectedCustomer.phone2 || "" },
				});
				handleFieldChange({
					target: { name: "phone3", value: selectedCustomer.phone3 || "" },
				});
				handleFieldChange({
					target: { name: "email", value: selectedCustomer.email || "" },
				});
				handleFieldChange({
					target: { name: "address", value: selectedCustomer.address || "" },
				});
				handleFieldChange({
					target: { name: "address2", value: selectedCustomer.address2 || "" },
				});
				handleFieldChange({
					target: { name: "address3", value: selectedCustomer.address3 || "" },
				});
			}
		}
	}, [formData.customer_id, customers, handleFieldChange]);

	// تعيين التاريخ والوقت الحالي تلقائياً عند فتح النموذج
	useEffect(() => {
		if (!dateInitialized) {
			const now = new Date();
			const year = now.getFullYear();
			const month = String(now.getMonth() + 1).padStart(2, "0");
			const day = String(now.getDate()).padStart(2, "0");
			const hours = String(now.getHours()).padStart(2, "0");
			const minutes = String(now.getMinutes()).padStart(2, "0");
			const currentDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;

			handleFieldChange({
				target: { name: "date", value: currentDateTime },
			});
			setDateInitialized(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dateInitialized]);

	// فلترة العملاء بناءً على البحث عن الاسم والهاتف
	const filteredCustomers = useMemo(() => {
		if (!searchInput.trim()) return customers;

		const searchLower = searchInput.toLowerCase();
		return customers.filter((customer) => {
			const name = customer.name?.toLowerCase() || "";
			const phone = customer.phone?.toLowerCase() || "";
			const phone2 = customer.phone2?.toLowerCase() || "";
			const phone3 = customer.phone3?.toLowerCase() || "";

			return (
				name.includes(searchLower) ||
				phone.includes(searchLower) ||
				phone2.includes(searchLower) ||
				phone3.includes(searchLower)
			);
		});
	}, [searchInput, customers]);

	const autocompleteListSx = {
		direction: "rtl",
		"& .MuiAutocomplete-option": {
			whiteSpace: "nowrap",
			overflow: "hidden",
			textOverflow: "ellipsis",
		},
	};

	const autocompleteSlotProps = {
		paper: {
			sx: {
				width: "100%",
				minWidth: 420,
				maxWidth: "90vw",
				direction: "rtl",
			},
		},
		popper: {
			sx: {
				minWidth: 420,
				maxWidth: "90vw",
				"& .MuiAutocomplete-paper": {
					width: "100%",
					minWidth: 420,
					maxWidth: "90vw",
				},
			},
		},
	};

	return (
		<Grid container spacing={2}>
			{formData.order_type === "tables" && (
				<Grid size={{ xs: 12, md: 1 }}>
					<TextField
						label="رقم الطاولة"
						name="table_id"
						type="number"
						value={formData.table_id}
						fullWidth
						InputProps={{ readOnly: true }}
						disabled={loading}
					/>
				</Grid>
			)}

			<Grid size={{ xs: 12, md: formData.order_type === "tables" ? 11 : 12 }}>
				<Autocomplete
					fullWidth
					sx={{ width: "100%" }}
					options={filteredCustomers}
					getOptionLabel={(option) =>
						`${option.name || `عميل #${option.id}`} (${option.phone || ""})`
					}
					value={
						customers.find((c) => c.id === formData.customer_id) || null
					}
					onChange={(event, newValue) => {
						if (newValue) {
							handleFieldChange({
								target: { name: "customer_id", value: newValue.id },
							});
						} else {
							handleFieldChange({
								target: { name: "customer_id", value: "" },
							});
						}
					}}
					inputValue={searchInput}
					onInputChange={(event, newInputValue) => {
						setSearchInput(newInputValue);
					}}
					loading={loadingCustomers}
					disabled={loading || loadingCustomers}
					ListboxProps={{ sx: autocompleteListSx }}
					slotProps={autocompleteSlotProps}
					renderOption={(props, option) => (
						<Box
							component="li"
							{...props}
							sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
						>
							{`${option.name || `عميل #${option.id}`} (${option.phone || ""})`}
						</Box>
					)}
					renderInput={(params) => (
						<TextField
							{...params}
							label="اختر العميل"
							placeholder="ابحث باسم أو هاتف"
							fullWidth
							sx={{ width: "100%" }}
							slotProps={{
								input: {
									...params.InputProps,
									endAdornment: (
										<>
											{loadingCustomers ? (
												<CircularProgress color="inherit" size={20} />
											) : null}
											{params.InputProps.endAdornment}
										</>
									),
								},
							}}
						/>
					)}
				/>
			</Grid>

			<Grid size={{ xs: 12, md: 6 }}>
				<TextField
					label="الاسم"
					name="name"
					value={formData.name}
					onChange={handleFieldChange}
					fullWidth
					required
					disabled={loading}
				/>
			</Grid>

			<Grid size={{ xs: 12, md: 6 }}>
				<TextField
					label="الهاتف"
					name="phone"
					value={formData.phone}
					onChange={handleFieldChange}
					fullWidth
					required
					disabled={loading}
				/>
			</Grid>

			<Grid size={{ xs: 12, md: 4 }}>
				<Button
					variant={showAdditionalFields ? "contained" : "outlined"}
					onClick={() => setShowAdditionalFields(!showAdditionalFields)}
					fullWidth
					disabled={loading}
				>
					{showAdditionalFields ? "إخفاء الحقول الإضافية" : "عرض الحقول الإضافية"}
				</Button>
			</Grid>

			{showAdditionalFields && (
				<Grid size={{ xs: 12, md: 4 }}>
					<TextField
						label="الهاتف 2"
						name="phone2"
						value={formData.phone2}
						onChange={handleFieldChange}
						fullWidth
						disabled={loading}
					/>
				</Grid>
			)}

			{showAdditionalFields && (
				<Grid size={{ xs: 12, md: 4 }}>
					<TextField
						label="الهاتف 3"
						name="phone3"
						value={formData.phone3}
						onChange={handleFieldChange}
						fullWidth
						disabled={loading}
					/>
				</Grid>
			)}

			<Grid size={{ xs: 12, md: 4 }}>
				<TextField
					label="البريد الإلكتروني"
					name="email"
					value={formData.email}
					onChange={handleFieldChange}
					fullWidth
					disabled={loading}
				/>
			</Grid>

			<Grid size={{ xs: 12, md: 4 }}>
				<TextField
					label="العنوان"
					name="address"
					value={formData.address}
					onChange={handleFieldChange}
					fullWidth
					disabled={loading}
				/>
			</Grid>

			{showAdditionalFields && (
				<Grid size={{ xs: 12, md: 4 }}>
					<TextField
						label="العنوان 2"
						name="address2"
						value={formData.address2}
						onChange={handleFieldChange}
						fullWidth
						disabled={loading}
					/>
				</Grid>
			)}

			{showAdditionalFields && (
				<Grid size={{ xs: 12, md: 4 }}>
					<TextField
						label="العنوان 3"
						name="address3"
						value={formData.address3}
						onChange={handleFieldChange}
						fullWidth
						disabled={loading}
					/>
				</Grid>
			)}

		{formData.customer_id && (
			<Grid size={{ xs: 12, md: 4 }}>
				<Button
					variant={showAdditionalAddresses ? "contained" : "outlined"}
					color="info"
					onClick={() => setShowAdditionalAddresses(!showAdditionalAddresses)}
					fullWidth
					disabled={loading}
					sx={{ py: 1.2, fontWeight: 600 }}
				>
					{showAdditionalAddresses ? "✓ إخفاء العناوين الإضافية" : "📍 تعديل العناوين الإضافية"}
				</Button>
			</Grid>
		)}

		{showAdditionalAddresses && (
			<Grid size={{ xs: 12, md: 4 }}>
				<TextField
					label="العنوان 2"
					name="address2"
					value={formData.address2}
					onChange={handleFieldChange}
					fullWidth
					disabled={loading}
				/>
			</Grid>
		)}

		{showAdditionalAddresses && (
			<Grid size={{ xs: 12, md: 4 }}>
				<TextField
					label="العنوان 3"
					name="address3"
					value={formData.address3}
					onChange={handleFieldChange}
					fullWidth
					disabled={loading}
				/>
			</Grid>
		)}

		{formData.order_type !== "delivery" && (
			<Grid size={{ xs: 12, md: 4 }}>
				<Autocomplete
					fullWidth
					sx={{ width: "100%" }}
					options={captains}
					getOptionLabel={(option) => option.name || `قائد #${option.id}`}
					value={
						captains.find((c) => c.id === formData.captain_order_id) || null
					}
					onChange={(event, newValue) => {
						if (newValue) {
							handleFieldChange({
								target: { name: "captain_order_id", value: newValue.id },
							});
						} else {
							handleFieldChange({
								target: { name: "captain_order_id", value: "" },
							});
						}
					}}
					loading={loadingCaptains}
					disabled={loading || loadingCaptains}
					ListboxProps={{ sx: autocompleteListSx }}
					slotProps={autocompleteSlotProps}
					renderOption={(props, option) => (
						<Box
							component="li"
							{...props}
							sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
						>
							{option.name || `قائد #${option.id}`}
						</Box>
					)}
					renderInput={(params) => (
						<TextField
							{...params}
							label="كابتن اوردر"
							fullWidth
							sx={{ width: "100%" }}
							slotProps={{
								input: {
									...params.InputProps,
									endAdornment: (
										<>
											{loadingCaptains ? (
												<CircularProgress color="inherit" size={20} />
											) : null}
											{params.InputProps.endAdornment}
										</>
									),
								},
							}}
						/>
					)}
				/>
			</Grid>
		)}

		{formData.order_type === "delivery" && (
			<Grid size={{ xs: 12, md: 4 }}>
				<Autocomplete
					fullWidth
					sx={{ width: "100%" }}
					options={deliveryUsers}
					getOptionLabel={(option) => option.name || `موظف توصيل #${option.id}`}
					value={
						deliveryUsers.find((d) => d.id === formData.delivery_id) || null
					}
					onChange={(event, newValue) => {
						if (newValue) {
							handleFieldChange({
								target: { name: "delivery_id", value: newValue.id },
							});
						} else {
							handleFieldChange({
								target: { name: "delivery_id", value: "" },
							});
						}
					}}
					loading={loadingDeliveryUsers}
					disabled={loading || loadingDeliveryUsers}
					ListboxProps={{ sx: autocompleteListSx }}
					slotProps={autocompleteSlotProps}
					renderOption={(props, option) => (
						<Box
							component="li"
							{...props}
							sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
						>
							{option.name || `موظف توصيل #${option.id}`}
						</Box>
					)}
					renderInput={(params) => (
						<TextField
							{...params}
							label="موظف التوصيل"
							fullWidth
							sx={{ width: "100%" }}
							slotProps={{
								input: {
									...params.InputProps,
									endAdornment: (
										<>
											{loadingDeliveryUsers ? (
												<CircularProgress color="inherit" size={20} />
											) : null}
											{params.InputProps.endAdornment}
										</>
									),
								},
							}}
						/>
					)}
				/>
			</Grid>
		)}
	</Grid>
);
};

export default OrderCustomerInfo;
